import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiActivity, FiLock, FiTerminal, FiBookOpen, FiCalendar, FiCheckSquare, FiCpu, FiFilm, FiGitBranch, FiGrid, FiMail, FiTrendingUp, FiHome, FiMessageSquare, FiEdit3, FiPlus, FiShield, FiTarget, FiUser, FiUsers, FiZap } from 'react-icons/fi';
import { ChatState } from '../Context/ChatProvider';
import { Avatar } from '../ui';
import { fetchTasks } from '../lib/tasks';
import { useWorkspace } from './Workspace';
import { t, t as i18nT } from '../i18n';

const PAGES = [
  { label: t("Dashboard"), to: '/dashboard', screen: 'dashboard', icon: FiHome },
  { label: t("Calendar"), to: '/scheduler', screen: 'calendar', icon: FiCalendar },
  { label: t("Chat"), to: '/chat', screen: 'chat', icon: FiMessageSquare },
  { label: t("Notes"), to: '/notes', screen: 'notes', icon: FiEdit3 },
  { label: t("Performance"), to: '/performance', icon: FiTrendingUp },
  { label: t("Virtual Office"), to: '/office', screen: 'office', icon: FiGrid },
  { label: t("GQ Library"), to: '/library', screen: 'library', icon: FiBookOpen },
  { label: t("Workers"), to: '/userManagement', screen: 'workers', icon: FiUsers },
  { label: t("Organization"), to: '/organization', screen: 'hr', icon: FiGitBranch },
  { label: t("Permissions"), to: '/admin/permissions', screen: 'permissions', icon: FiLock },
  { label: t("Email"), to: '/admin/email', screen: 'email', icon: FiMail },
  { label: t("Audit"), to: '/admin/audit', screen: 'audit', icon: FiActivity },
  { label: t("AI usage"), to: '/admin/ai-usage', screen: 'aiusage', icon: FiCpu },
  { label: t("Access control / My attendance"), to: '/access', screen: 'access', icon: FiShield },
  { label: t("Projects"), to: '/projects', screen: 'projects', icon: FiCheckSquare },
  { label: t("API Client"), to: '/apiclient', screen: 'apiclient', icon: FiTerminal },
  { label: t("Leads"), to: '/leads', screen: 'leads', icon: FiTarget },
  { label: t("Auto Generate"), to: '/autogen', screen: 'autogen', icon: FiZap },
  { label: t("QIRA — AI agent"), to: '/robot', screen: 'assistant', icon: FiCpu },
  { label: t("Cinema"), to: '/cinema', icon: FiFilm },
  { label: t("My profile"), to: '/profile', icon: FiUser },
  { label: t("Create issue"), to: '/tasks?create=1', icon: FiPlus },
];

export default function CommandPalette() {
  const { users, projects, setPaletteOpen, can, levels } = useWorkspace();
  const { address } = ChatState();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [tasks, setTasks] = useState([]);
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    fetchTasks(address).then(setTasks).catch(() => {});
  }, [address]);

  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    const has = (s) => (s || '').toLowerCase().includes(t);
    const out = [];
    PAGES.filter((p) => (!p.screen || (can(p.screen) && (!['permissions'].includes(p.screen) || (levels && levels[p.screen] !== 'none')))) && (!t || has(p.label))).forEach((p) =>
      out.push({ group: i18nT("Pages"), key: p.to, label: p.label, icon: <p.icon />, go: p.to })
    );
    if (t) {
      tasks
        .filter((x) => has(x.summary) || has(x.key || `task-${x.taskNum}`) || has(x.assigneeTo))
        .slice(0, 6)
        .forEach((x) =>
          out.push({ group: i18nT("Issues"), key: x._id, label: x.summary, sub: `${x.key || `TASK-${x.taskNum}`} · ${x.statux}`, icon: <FiCheckSquare />, go: x.key ? `/projects/${x.key.split('-')[0]}/board?selectedIssue=${x.key}` : `/tasks?task=${x.taskNum}` })
        );
      projects
        .filter((p) => has(p.name) || has(p.key))
        .slice(0, 4)
        .forEach((p) => out.push({ group: i18nT("Projects"), key: p._id, label: `${p.icon} ${p.name}`, sub: p.key, icon: <FiCheckSquare />, go: `/projects/${p.key}/board` }));
      users
        .filter((u) => has(u.fullname) || has(u.email) || has(u.role))
        .slice(0, 6)
        .forEach((u) =>
          out.push({ group: i18nT("Workers"), key: u._id, label: u.fullname, sub: u.role, icon: <Avatar name={u.fullname} src={u.img_user} size={20} />, go: `/userManagement?q=${encodeURIComponent(u.fullname)}` })
        );
    }
    return out;
  }, [q, tasks, users, projects, levels]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => setSel(0), [q]);

  const run = (row) => {
    if (!row) return;
    setPaletteOpen(false);
    navigate(row.go);
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    else if (e.key === 'Enter') run(rows[sel]);
    else if (e.key === 'Escape') setPaletteOpen(false);
  };

  let lastGroup = null;
  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && setPaletteOpen(false)}>
      <div className="palette">
        <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder={t("Type a command, task number, or a person's name…")} />
        <div className="palette-list">
          {rows.length === 0 && <div className="empty">{t("No results for “")}{q}”</div>}
          {rows.map((r, i) => {
            const head = r.group !== lastGroup ? <div className="palette-group">{r.group}</div> : null;
            lastGroup = r.group;
            return (
              <React.Fragment key={`${r.group}-${r.key}`}>
                {head}
                <div className={`palette-row ${i === sel ? 'sel' : ''}`} onMouseEnter={() => setSel(i)} onClick={() => run(r)}>
                  {r.icon}
                  <span>{r.label}</span>
                  {r.sub && <span className="sub">{r.sub}</span>}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
